import React from 'react';
import { Popover, Card, Button, Form, Input, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';

const cache: CellMeasurerCache = new CellMeasurerCache({ defaultHeight: 30, fixedWidth: true });

type TableFilterPopoverProps = {
  children: React.ReactNode;
  values: {
    id: number;
    name: string;
  }[];
  initiallySelectedIds: number[];
  handleChange: (selectedIds: number[]) => void;
  onOpen?: () => void;
  onClose?: () => void;
  showSearch?: boolean;
  height?: number;
};

const TableFilterPopover: React.FC<TableFilterPopoverProps> = props => {
  const { t } = useTranslation();

  const [popoverVisible, setPopoverVisible] = React.useState(false);
  const [keywords, setKeywords] = React.useState('');

  const formMethods = useForm({
    defaultValues: {
      selectedIds: props.initiallySelectedIds,
    },
    mode: 'onTouched',
  });

  const { handleSubmit, setValue, watch, formState, reset } = formMethods;
  const selectedIds = watch('selectedIds');

  React.useEffect(() => {
    reset({ selectedIds: props.initiallySelectedIds });
  }, [props.initiallySelectedIds, reset]);

  React.useEffect(() => {
    setTimeout(() => {
      const input = document.getElementById('brandSearch');
      if (popoverVisible && input) input.focus();
    }, 100);
  }, [popoverVisible]);

  React.useEffect(() => {
    if (popoverVisible) handleResize();
  }, [popoverVisible]);

  const handleVisibleChange = (visible: boolean) => {
    setKeywords('');
    setPopoverVisible(visible);
    if (visible && props.onOpen) props.onOpen();
    else if (props.onClose) props.onClose();
  };

  const handleResize = () => {
    cache.clearAll();
  };

  const data = props.values.filter(value =>
    value.name.toLowerCase().includes(keywords.toLowerCase())
  );

  const onSubmit = (values: { selectedIds: number[] }) => {
    props.handleChange(values.selectedIds);
    handleVisibleChange(false);
  };

  const renderRow = ({ index, parent, key, style }: any) => {
    const row = data[index];
    const selected = selectedIds.includes(row.id);

    return (
      <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
        <div
          key={key}
          style={style}
          className="row table-filter-popover__search-table_row flex"
          onClick={() =>
            setValue(
              'selectedIds',
              selected ? selectedIds.filter(id => id !== row.id) : [...selectedIds, row.id].sort(),
              {
                shouldDirty: true,
              }
            )
          }
        >
          <div>
            <Checkbox checked={selected} />
          </div>
          <div className="flex-1 pl-3">{row.name}</div>
        </div>
      </CellMeasurer>
    );
  };

  return (
    <Popover
      open={popoverVisible}
      onOpenChange={visible => handleVisibleChange(visible)}
      overlayClassName="table-filter-popover"
      placement="bottomLeft"
      destroyTooltipOnHide
      content={
        <Card>
          <FormProvider {...formMethods}>
            <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
              <div className="table-popover-search">
                {props.showSearch && (
                  <Input.Search
                    id="brandSearch"
                    value={keywords}
                    onChange={e => {
                      setKeywords(e.target.value);
                      handleResize();
                    }}
                  />
                )}
              </div>

              <div className="table-filter-popover__search-table">
                <List
                  deferredMeasurementCache={cache}
                  height={props.height || 300}
                  rowCount={data.length}
                  rowHeight={cache.rowHeight}
                  rowRenderer={renderRow}
                  width={300}
                />
              </div>

              <div className="flex-col items-end mt-3 mr-3">
                <div>
                  <Button
                    onClick={() => {
                      setValue('selectedIds', []);
                      handleSubmit(onSubmit)();
                    }}
                    className="drawer-submit__bottom-cancel"
                  >
                    {t('common:reset')}
                  </Button>
                  <Button type="primary" htmlType="submit" disabled={!formState.isDirty}>
                    {t('common:ok')}
                  </Button>
                </div>
              </div>
            </Form>
          </FormProvider>
        </Card>
      }
      trigger="click"
    >
      {props.children}
    </Popover>
  );
};

export default TableFilterPopover;
